import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { AppContext } from '@r-app/app-context';
import { BlueprintPageAnnotationText } from './blueprint-page-annotation-text';
import { toAnnotationPresetRecords } from '../helpers/place-annotation-preset';
import { SkuAnnotationArrowhead, SkuAnnotationLineStyle, SkuAnnotationType, type SkuAnnotation } from '@rta/rta-blueprint-library';
import {
	type AnnotationPreviewPatch,
	DEFAULT_ANNOTATION_FILL,
	DEFAULT_ANNOTATION_INK,
	getAnnotationFillPaint,
	getAnnotationStrokePaint,
	isAnnotationFilled,
	useAnnotationStylePreview,
	useSkuAnnotation
} from '../../sku-annotations';

export type BlueprintPageAnnotationOverlayProps = {
	pageNumber: number;
	children: React.ReactNode;
};

export const BLUEPRINT_PAGE_SVG_W = 1100;
const SVG_W = BLUEPRINT_PAGE_SVG_W;
export const BLUEPRINT_PAGE_SVG_H = 850;
const SVG_H = BLUEPRINT_PAGE_SVG_H;
const MIN_DRAG = 3;
const MIN_LINE = 5;
const HANDLE_SIZE = 8;

type DragState = {
	annotationId: string;
	mode: 'body' | 'start-endpoint' | 'end-endpoint' | 'resize';
	startClientX: number;
	startClientY: number;
	origX: number;
	origY: number;
	origX2: number;
	origY2: number;
	origW: number;
	origH: number;
	resizeHandle?: string;
	// Other multi-selected annotations to drag along with this one (body drags only)
	groupOrigins?: Array<{ id: string; x: number; y: number; x2: number; y2: number }>;
};

type DragDelta = { dx: number; dy: number };

type RotateState = { annotationId: string; startAngle: number; startRotation: number };

const getDashArray = (ls: SkuAnnotationLineStyle): string | undefined => {
	if (ls === SkuAnnotationLineStyle.Dashed) return '8,6';
	if (ls === SkuAnnotationLineStyle.Dotted) return '2,4';
	return undefined;
};

/** A text box's border is a fixed hairline on this surface, so only whether it is stroked at all is read */
const TEXT_BOX_STROKE_WIDTH = 1;

// The annotation instance mutates in place, so the memoized text box gets every field it reads flattened into one comparable prop
const getTextSignature = (a: SkuAnnotation): string => JSON.stringify([
	a.x, a.y, a.width, a.height, a.text, a.fontSize, a.textColor, a.color, a.fontFamily, a.fontWeight, a.fontStyle,
	a.styleRuns, a.colorRuns
]);

// Rotation pivot: box-shaped annotations rotate about their box center, Line about its midpoint
const getAnnotationCenter = (a: SkuAnnotation): { cx: number; cy: number } => {
	if (a.type === SkuAnnotationType.Line) return { cx: (a.x + a.x2) / 2, cy: (a.y + a.y2) / 2 };
	return { cx: a.x + a.width / 2, cy: a.y + a.height / 2 };
};

const ROTATE_HANDLE_DISTANCE = HANDLE_SIZE * 3;
const ROTATE_HANDLE_RADIUS = HANDLE_SIZE * 0.75;

/**
 * Locate the `[data-blueprint-page]` element for this page so the overlay `<svg>` can portal
 * INSIDE it. The page div is what `exportBlueprintToPdf`/`exportBlueprintToPptx` hand to
 * html2canvas — a sibling SVG (the old wrapper layout) never made it into the exported PDF.
 * Rendering the SVG as a descendant keeps text boxes and other annotations in the capture.
 */
const usePagePortalTarget = (pageNumber: number): HTMLElement | null => {
	const [target, setTarget] = useState<HTMLElement | null>(null);

	useLayoutEffect(() => {
		const el = document.querySelector<HTMLElement>(`[data-blueprint-page="${pageNumber}"]`);
		setTarget(el);
	}, [pageNumber]);

	return target;
};

/**
 * Lightweight gatekeeper — renders ONLY children unless the overlay is actually needed.
 * Pages without annotations and no active draw tool pay zero overhead.
 * Pages without annotations but with a draw tool active get a minimal draw-only layer.
 * Pages with annotations get the full overlay.
 */
export const BlueprintPageAnnotationOverlay = (props: BlueprintPageAnnotationOverlayProps): React.ReactElement => {
	const { pageNumber, children } = props;
	const { controller } = useContext(AppContext) ?? {};
	const kitchen = controller?.kitchen;
	const { activeTool } = useSkuAnnotation();

	const [hasAnnotations, setHasAnnotations] = useState(() => {
		return (kitchen?.blueprintAnnotations?.children ?? []).some((a) => a.pageNumber === pageNumber);
	});

	useEffect(() => {
		if (!kitchen) return;
		const check = (): void => {
			const any = (kitchen.blueprintAnnotations?.children ?? []).some((a) => a.pageNumber === pageNumber);
			setHasAnnotations(any);
		};
		const subId = kitchen.subscribe('sku_annotations', '*', check);
		return () => { kitchen.unsubscribe(subId); };
	}, [kitchen, pageNumber]);

	const isDrawTool = activeTool !== 'select';

	// No annotations, no draw tool — just render children, zero overhead
	if (!isDrawTool && !hasAnnotations) {
		return <>{children}</>;
	}

	// Draw tool active but no annotations — use minimal draw-only overlay
	if (isDrawTool && !hasAnnotations) {
		return (
			<DrawOnlyOverlay pageNumber={pageNumber}>
				{children}
			</DrawOnlyOverlay>
		);
	}

	// Has annotations — mount full overlay
	return (
		<FullAnnotationOverlay pageNumber={pageNumber}>
			{children}
		</FullAnnotationOverlay>
	);
};

// ============================================================
// Minimal draw-only overlay — just capture rect + preview elements
// No subscriptions, no drag logic, no annotation rendering
// ============================================================

const DrawOnlyOverlay = (props: BlueprintPageAnnotationOverlayProps): React.ReactElement => {
	const { pageNumber, children } = props;
	const { controller } = useContext(AppContext) ?? {};
	const kitchen = controller?.kitchen;
	const { activeTool, setActiveTool, setSelectedAnnotationId, setEditingAnnotationId } = useSkuAnnotation();

	const portalTarget = usePagePortalTarget(pageNumber);
	const svgRef = useRef<SVGSVGElement | null>(null);
	const isDrawingRef = useRef(false);
	const drawModeRef = useRef<string>('none');
	const startRef = useRef<{ x: number; y: number } | null>(null);
	const lastPosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
	const doodlePointsRef = useRef<Array<{ x: number; y: number }>>([]);

	const linePreviewRef = useRef<SVGLineElement | null>(null);
	const rectPreviewRef = useRef<SVGRectElement | null>(null);
	const ellipsePreviewRef = useRef<SVGEllipseElement | null>(null);
	const textPreviewRef = useRef<SVGRectElement | null>(null);
	const doodlePreviewRef = useRef<SVGPathElement | null>(null);

	const screenToSvg = useCallback((clientX: number, clientY: number): { x: number; y: number } => {
		const svg = svgRef.current;
		if (!svg) return { x: 0, y: 0 };
		const pt = svg.createSVGPoint();
		pt.x = clientX;
		pt.y = clientY;
		const ctm = svg.getScreenCTM();
		if (!ctm) return { x: 0, y: 0 };
		const svgPt = pt.matrixTransform(ctm.inverse());
		return { x: svgPt.x, y: svgPt.y };
	}, []);

	const hideAllPreviews = useCallback((): void => {
		linePreviewRef.current?.setAttribute('visibility', 'hidden');
		rectPreviewRef.current?.setAttribute('visibility', 'hidden');
		ellipsePreviewRef.current?.setAttribute('visibility', 'hidden');
		textPreviewRef.current?.setAttribute('visibility', 'hidden');
		doodlePreviewRef.current?.setAttribute('visibility', 'hidden');
	}, []);

	const handleMouseDown = useCallback((e: React.MouseEvent): void => {
		if (activeTool === 'select' || !kitchen) return;
		e.preventDefault();
		e.stopPropagation();
		const pos = screenToSvg(e.clientX, e.clientY);
		startRef.current = pos;
		lastPosRef.current = pos;
		isDrawingRef.current = true;
		drawModeRef.current = activeTool;

		if (activeTool === 'doodle') { doodlePointsRef.current = [pos]; doodlePreviewRef.current?.setAttribute('d', `M ${pos.x} ${pos.y}`); doodlePreviewRef.current?.setAttribute('visibility', 'visible'); }
		if (activeTool === 'line' && linePreviewRef.current) { linePreviewRef.current.setAttribute('x1', String(pos.x)); linePreviewRef.current.setAttribute('y1', String(pos.y)); linePreviewRef.current.setAttribute('x2', String(pos.x)); linePreviewRef.current.setAttribute('y2', String(pos.y)); linePreviewRef.current.setAttribute('visibility', 'visible'); }
		if (activeTool === 'textbox' && textPreviewRef.current) { textPreviewRef.current.setAttribute('x', String(pos.x)); textPreviewRef.current.setAttribute('y', String(pos.y)); textPreviewRef.current.setAttribute('width', '0'); textPreviewRef.current.setAttribute('height', '0'); textPreviewRef.current.setAttribute('visibility', 'visible'); }
		if (activeTool === 'rectangle' && rectPreviewRef.current) { rectPreviewRef.current.setAttribute('visibility', 'visible'); }
		if (activeTool === 'ellipse' && ellipsePreviewRef.current) { ellipsePreviewRef.current.setAttribute('visibility', 'visible'); }
	}, [activeTool, kitchen, screenToSvg]);

	const handleMouseMove = useCallback((e: React.MouseEvent): void => {
		if (!isDrawingRef.current) return;
		const pos = screenToSvg(e.clientX, e.clientY);
		lastPosRef.current = pos;
		const start = startRef.current;
		if (!start) return;
		const mode = drawModeRef.current;

		if (mode === 'doodle' && doodlePreviewRef.current) { doodlePointsRef.current.push(pos); doodlePreviewRef.current.setAttribute('d', doodlePointsRef.current.reduce((p, pt, i) => i === 0 ? `M ${pt.x} ${pt.y}` : `${p} L ${pt.x} ${pt.y}`, '')); }
		if (mode === 'line' && linePreviewRef.current) { linePreviewRef.current.setAttribute('x2', String(pos.x)); linePreviewRef.current.setAttribute('y2', String(pos.y)); }
		if (mode === 'textbox' && textPreviewRef.current) { textPreviewRef.current.setAttribute('x', String(Math.min(pos.x, start.x))); textPreviewRef.current.setAttribute('y', String(Math.min(pos.y, start.y))); textPreviewRef.current.setAttribute('width', String(Math.abs(pos.x - start.x))); textPreviewRef.current.setAttribute('height', String(Math.abs(pos.y - start.y))); }
		if (mode === 'rectangle' && rectPreviewRef.current) { rectPreviewRef.current.setAttribute('x', String(Math.min(pos.x, start.x))); rectPreviewRef.current.setAttribute('y', String(Math.min(pos.y, start.y))); rectPreviewRef.current.setAttribute('width', String(Math.abs(pos.x - start.x))); rectPreviewRef.current.setAttribute('height', String(Math.abs(pos.y - start.y))); }
		if (mode === 'ellipse' && ellipsePreviewRef.current) { ellipsePreviewRef.current.setAttribute('cx', String((pos.x + start.x) / 2)); ellipsePreviewRef.current.setAttribute('cy', String((pos.y + start.y) / 2)); ellipsePreviewRef.current.setAttribute('rx', String(Math.abs(pos.x - start.x) / 2)); ellipsePreviewRef.current.setAttribute('ry', String(Math.abs(pos.y - start.y) / 2)); }
	}, [screenToSvg]);

	const handleMouseUp = useCallback((): void => {
		if (!isDrawingRef.current || !kitchen) return;
		const mode = drawModeRef.current;
		const start = startRef.current;
		const pos = lastPosRef.current;
		isDrawingRef.current = false;
		drawModeRef.current = 'none';
		startRef.current = null;
		hideAllPreviews();
		if (!start) return;

		const common = { pageNumber, fontSize: 14, color: DEFAULT_ANNOTATION_INK, strokeWidth: 2, lineStyle: SkuAnnotationLineStyle.Solid, arrowhead: SkuAnnotationArrowhead.None };

		if (mode === 'textbox') {
			const w = Math.abs(pos.x - start.x); const h = Math.abs(pos.y - start.y);
			const isDrag = w > MIN_DRAG || h > MIN_DRAG;
			const id = kitchen.blueprintAnnotations.add({ type: SkuAnnotationType.TextBox, x: Math.min(pos.x, start.x), y: Math.min(pos.y, start.y), width: isDrag ? Math.max(60, w) : 120, height: isDrag ? Math.max(20, h) : 30, text: '', ...common, backgroundColor: DEFAULT_ANNOTATION_FILL, textColor: DEFAULT_ANNOTATION_INK, fontFamily: 'Arial' });
			if (typeof id === 'string') { setSelectedAnnotationId(id); setEditingAnnotationId(id); }
		}
		if (mode === 'doodle') {
			const pts = doodlePointsRef.current;
			if (pts.length >= 2) { const minX = Math.min(...pts.map((p) => p.x)); const minY = Math.min(...pts.map((p) => p.y)); kitchen.blueprintAnnotations.add({ type: SkuAnnotationType.Doodle, x: minX, y: minY, width: 0, height: 0, text: '', points: pts.map((p) => ({ x: p.x - minX, y: p.y - minY })), ...common, color: '#e53e3e' }); }
			doodlePointsRef.current = []; doodlePreviewRef.current?.setAttribute('d', '');
		}
		if (mode === 'line') { const dx = pos.x - start.x; const dy = pos.y - start.y; if (Math.sqrt(dx * dx + dy * dy) >= MIN_LINE) { kitchen.blueprintAnnotations.add({ type: SkuAnnotationType.Line, x: start.x, y: start.y, x2: pos.x, y2: pos.y, width: 0, height: 0, text: '', ...common }); } }
		if (mode === 'rectangle') { const w = Math.abs(pos.x - start.x); const h = Math.abs(pos.y - start.y); if (w >= MIN_DRAG && h >= MIN_DRAG) { kitchen.blueprintAnnotations.add({ type: SkuAnnotationType.Rectangle, x: Math.min(pos.x, start.x), y: Math.min(pos.y, start.y), width: w, height: h, text: '', ...common }); } }
		if (mode === 'ellipse') { const w = Math.abs(pos.x - start.x); const h = Math.abs(pos.y - start.y); if (w >= MIN_DRAG && h >= MIN_DRAG) { kitchen.blueprintAnnotations.add({ type: SkuAnnotationType.Ellipse, x: Math.min(pos.x, start.x), y: Math.min(pos.y, start.y), width: w, height: h, text: '', ...common }); } }

		setActiveTool('select');
	}, [kitchen, pageNumber, hideAllPreviews, setSelectedAnnotationId, setEditingAnnotationId, setActiveTool]);

	const overlaySvg = (
		<svg ref={svgRef} viewBox={`0 0 ${SVG_W} ${SVG_H}`} preserveAspectRatio="none"
			style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'auto', cursor: 'crosshair', zIndex: 5 }}
			onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}
		>
			<rect ref={textPreviewRef} x={0} y={0} width={0} height={0} fill="rgba(49,130,206,0.08)" stroke="#3182ce" strokeWidth={1} strokeDasharray="4,3" visibility="hidden" style={{ pointerEvents: 'none' }} />
			<path ref={doodlePreviewRef} d="" fill="none" stroke="#e53e3e" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" visibility="hidden" style={{ pointerEvents: 'none' }} />
			<line ref={linePreviewRef} x1={0} y1={0} x2={0} y2={0} stroke="#3182ce" strokeWidth={1} strokeDasharray="4,3" visibility="hidden" style={{ pointerEvents: 'none' }} />
			<rect ref={rectPreviewRef} x={0} y={0} width={0} height={0} fill="none" stroke="#3182ce" strokeWidth={1} strokeDasharray="4,3" visibility="hidden" style={{ pointerEvents: 'none' }} />
			<ellipse ref={ellipsePreviewRef} cx={0} cy={0} rx={0} ry={0} fill="none" stroke="#3182ce" strokeWidth={1} strokeDasharray="4,3" visibility="hidden" style={{ pointerEvents: 'none' }} />
		</svg>
	);

	return (
		<>
			{children}
			{portalTarget ? createPortal(overlaySvg, portalTarget) : null}
		</>
	);
};

// ============================================================
// Full annotation overlay — annotations + drawing + drag/resize
// Only mounted for pages that actually have annotations
// ============================================================

const FullAnnotationOverlay = (props: BlueprintPageAnnotationOverlayProps): React.ReactElement => {
	const { pageNumber, children } = props;
	const { controller } = useContext(AppContext) ?? {};
	const kitchen = controller?.kitchen;
	const { activeTool, setActiveTool, selectedAnnotationId, setSelectedAnnotationId, selectedAnnotationIds, setSelectedAnnotationIds, toggleAnnotationSelection, setEditingAnnotationId, editingAnnotationId, setActiveTextSelection, setFormatSelection, contextMenu, setContextMenu, pendingSaveRef, onSaveAnnotationPreset } = useSkuAnnotation();
	// A preview is only ever set for the toolbar's own selection, and subscribing by id keeps a picker drag from re-rendering the pages that don't own the annotation
	const previewedAnnotationId = selectedAnnotationId && kitchen?.blueprintAnnotations?.findById(selectedAnnotationId)?.pageNumber === pageNumber ? selectedAnnotationId : null;
	const stylePreview = useAnnotationStylePreview(previewedAnnotationId);

	const portalTarget = usePagePortalTarget(pageNumber);
	const svgRef = useRef<SVGSVGElement | null>(null);
	const [version, setVersion] = useState(0);

	const isDrawingRef = useRef(false);
	const drawModeRef = useRef<string>('none');
	const startRef = useRef<{ x: number; y: number } | null>(null);
	const lastPosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
	const doodlePointsRef = useRef<Array<{ x: number; y: number }>>([]);

	const linePreviewRef = useRef<SVGLineElement | null>(null);
	const rectPreviewRef = useRef<SVGRectElement | null>(null);
	const ellipsePreviewRef = useRef<SVGEllipseElement | null>(null);
	const textPreviewRef = useRef<SVGRectElement | null>(null);
	const doodlePreviewRef = useRef<SVGPathElement | null>(null);

	const annotationGroupRefs = useRef<Map<string, SVGGElement>>(new Map());
	const dragStateRef = useRef<DragState | null>(null);
	const dragDeltaRef = useRef<DragDelta>({ dx: 0, dy: 0 });
	// startDrag only stopPropagation()s the mousedown, so a completed drag/rotate still fires a trailing click; consumed once by handleClick so it never collapses a multi-selection to the dragged item
	const suppressNextClickRef = useRef(false);
	const [dragState, setDragState] = useState<DragState | null>(null);
	const [rotateState, setRotateState] = useState<RotateState | null>(null);

	const bumpVersion = useCallback((): void => setVersion((v) => v + 1), []);

	useEffect(() => {
		if (!kitchen) return;
		const subId = kitchen.subscribe('sku_annotations', '*', bumpVersion);
		// singular noun: property setters (fontSize/color/bold/italic…) publish 'sku_annotation' — needed for live toolbar edits, but scoped to this page so editing an annotation on page 3 doesn't rebuild page 1's foreignObjects
		const propSubId = kitchen.subscribe('sku_annotation', '*', (event) => {
			const ref = (event as { payload?: { ref?: unknown } }).payload?.ref;
			if (typeof ref === 'string') {
				const owner = kitchen.blueprintAnnotations?.findById(ref);
				if (owner && owner.pageNumber !== pageNumber) return;
			}
			bumpVersion();
		});
		return () => { kitchen.unsubscribe(subId); kitchen.unsubscribe(propSubId); };
	}, [kitchen, pageNumber, bumpVersion]);

	const annotations = useMemo(
		() => (kitchen?.blueprintAnnotations?.children ?? []).filter((a) => a.pageNumber === pageNumber),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[kitchen, pageNumber, version]
	);

	const screenToSvg = useCallback((clientX: number, clientY: number): { x: number; y: number } => {
		const svg = svgRef.current;
		if (!svg) return { x: 0, y: 0 };
		const pt = svg.createSVGPoint();
		pt.x = clientX; pt.y = clientY;
		const ctm = svg.getScreenCTM();
		if (!ctm) return { x: 0, y: 0 };
		const svgPt = pt.matrixTransform(ctm.inverse());
		return { x: svgPt.x, y: svgPt.y };
	}, []);

	const hideAllPreviews = useCallback((): void => {
		linePreviewRef.current?.setAttribute('visibility', 'hidden');
		rectPreviewRef.current?.setAttribute('visibility', 'hidden');
		ellipsePreviewRef.current?.setAttribute('visibility', 'hidden');
		textPreviewRef.current?.setAttribute('visibility', 'hidden');
		doodlePreviewRef.current?.setAttribute('visibility', 'hidden');
	}, []);

	// === DRAWING ===
	const handleDrawMouseDown = useCallback((e: React.MouseEvent): void => {
		if (activeTool === 'select' || !kitchen) return;
		e.preventDefault(); e.stopPropagation();
		const pos = screenToSvg(e.clientX, e.clientY);
		startRef.current = pos; lastPosRef.current = pos; isDrawingRef.current = true; drawModeRef.current = activeTool;

		if (activeTool === 'doodle') { doodlePointsRef.current = [pos]; doodlePreviewRef.current?.setAttribute('d', `M ${pos.x} ${pos.y}`); doodlePreviewRef.current?.setAttribute('visibility', 'visible'); }
		if (activeTool === 'line' && linePreviewRef.current) { linePreviewRef.current.setAttribute('x1', String(pos.x)); linePreviewRef.current.setAttribute('y1', String(pos.y)); linePreviewRef.current.setAttribute('x2', String(pos.x)); linePreviewRef.current.setAttribute('y2', String(pos.y)); linePreviewRef.current.setAttribute('visibility', 'visible'); }
		if (activeTool === 'textbox' && textPreviewRef.current) { textPreviewRef.current.setAttribute('x', String(pos.x)); textPreviewRef.current.setAttribute('y', String(pos.y)); textPreviewRef.current.setAttribute('width', '0'); textPreviewRef.current.setAttribute('height', '0'); textPreviewRef.current.setAttribute('visibility', 'visible'); }
		if (activeTool === 'rectangle' && rectPreviewRef.current) { rectPreviewRef.current.setAttribute('visibility', 'visible'); }
		if (activeTool === 'ellipse' && ellipsePreviewRef.current) { ellipsePreviewRef.current.setAttribute('visibility', 'visible'); }
	}, [activeTool, kitchen, screenToSvg]);

	const handleDrawMouseMove = useCallback((e: React.MouseEvent): void => {
		if (!isDrawingRef.current) return;
		const pos = screenToSvg(e.clientX, e.clientY);
		lastPosRef.current = pos;
		const start = startRef.current;
		if (!start) return;
		const mode = drawModeRef.current;

		if (mode === 'doodle' && doodlePreviewRef.current) { doodlePointsRef.current.push(pos); doodlePreviewRef.current.setAttribute('d', doodlePointsRef.current.reduce((p, pt, i) => i === 0 ? `M ${pt.x} ${pt.y}` : `${p} L ${pt.x} ${pt.y}`, '')); }
		if (mode === 'line' && linePreviewRef.current) { linePreviewRef.current.setAttribute('x2', String(pos.x)); linePreviewRef.current.setAttribute('y2', String(pos.y)); }
		if (mode === 'textbox' && textPreviewRef.current) { textPreviewRef.current.setAttribute('x', String(Math.min(pos.x, start.x))); textPreviewRef.current.setAttribute('y', String(Math.min(pos.y, start.y))); textPreviewRef.current.setAttribute('width', String(Math.abs(pos.x - start.x))); textPreviewRef.current.setAttribute('height', String(Math.abs(pos.y - start.y))); }
		if (mode === 'rectangle' && rectPreviewRef.current) { rectPreviewRef.current.setAttribute('x', String(Math.min(pos.x, start.x))); rectPreviewRef.current.setAttribute('y', String(Math.min(pos.y, start.y))); rectPreviewRef.current.setAttribute('width', String(Math.abs(pos.x - start.x))); rectPreviewRef.current.setAttribute('height', String(Math.abs(pos.y - start.y))); }
		if (mode === 'ellipse' && ellipsePreviewRef.current) { ellipsePreviewRef.current.setAttribute('cx', String((pos.x + start.x) / 2)); ellipsePreviewRef.current.setAttribute('cy', String((pos.y + start.y) / 2)); ellipsePreviewRef.current.setAttribute('rx', String(Math.abs(pos.x - start.x) / 2)); ellipsePreviewRef.current.setAttribute('ry', String(Math.abs(pos.y - start.y) / 2)); }
	}, [screenToSvg]);

	const handleDrawMouseUp = useCallback((): void => {
		if (!isDrawingRef.current || !kitchen) return;
		const mode = drawModeRef.current;
		const start = startRef.current;
		const pos = lastPosRef.current;
		isDrawingRef.current = false; drawModeRef.current = 'none'; startRef.current = null;
		hideAllPreviews();
		if (!start) return;

		const common = { pageNumber, fontSize: 14, color: DEFAULT_ANNOTATION_INK, strokeWidth: 2, lineStyle: SkuAnnotationLineStyle.Solid, arrowhead: SkuAnnotationArrowhead.None };

		if (mode === 'textbox') {
			const w = Math.abs(pos.x - start.x); const h = Math.abs(pos.y - start.y);
			const isDrag = w > MIN_DRAG || h > MIN_DRAG;
			const id = kitchen.blueprintAnnotations.add({ type: SkuAnnotationType.TextBox, x: Math.min(pos.x, start.x), y: Math.min(pos.y, start.y), width: isDrag ? Math.max(60, w) : 120, height: isDrag ? Math.max(20, h) : 30, text: '', ...common, backgroundColor: DEFAULT_ANNOTATION_FILL, textColor: DEFAULT_ANNOTATION_INK, fontFamily: 'Arial' });
			if (typeof id === 'string') { setSelectedAnnotationId(id); setEditingAnnotationId(id); }
		}
		if (mode === 'doodle') { const pts = doodlePointsRef.current; if (pts.length >= 2) { const minX = Math.min(...pts.map((p) => p.x)); const minY = Math.min(...pts.map((p) => p.y)); kitchen.blueprintAnnotations.add({ type: SkuAnnotationType.Doodle, x: minX, y: minY, width: 0, height: 0, text: '', points: pts.map((p) => ({ x: p.x - minX, y: p.y - minY })), ...common, color: '#e53e3e' }); } doodlePointsRef.current = []; doodlePreviewRef.current?.setAttribute('d', ''); }
		if (mode === 'line') { const dx = pos.x - start.x; const dy = pos.y - start.y; if (Math.sqrt(dx * dx + dy * dy) >= MIN_LINE) { kitchen.blueprintAnnotations.add({ type: SkuAnnotationType.Line, x: start.x, y: start.y, x2: pos.x, y2: pos.y, width: 0, height: 0, text: '', ...common }); } }
		if (mode === 'rectangle') { const w = Math.abs(pos.x - start.x); const h = Math.abs(pos.y - start.y); if (w >= MIN_DRAG && h >= MIN_DRAG) { kitchen.blueprintAnnotations.add({ type: SkuAnnotationType.Rectangle, x: Math.min(pos.x, start.x), y: Math.min(pos.y, start.y), width: w, height: h, text: '', ...common }); } }
		if (mode === 'ellipse') { const w = Math.abs(pos.x - start.x); const h = Math.abs(pos.y - start.y); if (w >= MIN_DRAG && h >= MIN_DRAG) { kitchen.blueprintAnnotations.add({ type: SkuAnnotationType.Ellipse, x: Math.min(pos.x, start.x), y: Math.min(pos.y, start.y), width: w, height: h, text: '', ...common }); } }

		setActiveTool('select');
	}, [kitchen, pageNumber, hideAllPreviews, setSelectedAnnotationId, setEditingAnnotationId, setActiveTool]);

	// === DRAG / RESIZE — direct DOM manipulation, commit on mouseup only ===
	useEffect(() => {
		if (!dragState || !kitchen) return;
		const ann = kitchen.blueprintAnnotations.findById(dragState.annotationId);
		if (!ann) return;
		const groupEl = annotationGroupRefs.current.get(dragState.annotationId) ?? null;
		dragStateRef.current = dragState;
		dragDeltaRef.current = { dx: 0, dy: 0 };

		const handleMouseMove = (e: MouseEvent): void => {
			const ds = dragStateRef.current;
			if (!ds) return;
			const cur = screenToSvg(e.clientX, e.clientY);
			const startSvg = screenToSvg(ds.startClientX, ds.startClientY);
			const dx = cur.x - startSvg.x; const dy = cur.y - startSvg.y;
			dragDeltaRef.current = { dx, dy };

			if (ds.mode === 'body' && groupEl) {
				const { cx, cy } = getAnnotationCenter(ann);
				const rotatePart = ann.rotation ? ` rotate(${ann.rotation}, ${cx}, ${cy})` : '';
				groupEl.setAttribute('transform', `translate(${dx}, ${dy})${rotatePart}`);
				ds.groupOrigins?.forEach((origin) => {
					const memberEl = annotationGroupRefs.current.get(origin.id);
					const member = kitchen.blueprintAnnotations.findById(origin.id);
					if (!memberEl || !member) return;
					const { cx: mcx, cy: mcy } = getAnnotationCenter(member);
					const memberRotatePart = member.rotation ? ` rotate(${member.rotation}, ${mcx}, ${mcy})` : '';
					memberEl.setAttribute('transform', `translate(${dx}, ${dy})${memberRotatePart}`);
				});
			} else if (groupEl) {
				if (ds.mode === 'start-endpoint') {
					const newX = ds.origX + dx; const newY = ds.origY + dy;
					groupEl.querySelectorAll('line[data-drag]').forEach((el) => { el.setAttribute('x1', String(newX)); el.setAttribute('y1', String(newY)); });
					const c = groupEl.querySelector('circle[data-endpoint="start"]'); c?.setAttribute('cx', String(newX)); c?.setAttribute('cy', String(newY));
				} else if (ds.mode === 'end-endpoint') {
					const newX2 = ds.origX2 + dx; const newY2 = ds.origY2 + dy;
					groupEl.querySelectorAll('line[data-drag]').forEach((el) => { el.setAttribute('x2', String(newX2)); el.setAttribute('y2', String(newY2)); });
					const c = groupEl.querySelector('circle[data-endpoint="end"]'); c?.setAttribute('cx', String(newX2)); c?.setAttribute('cy', String(newY2));
				} else if (ds.mode === 'resize') {
					const h = ds.resizeHandle;
					let newX = ds.origX, newY = ds.origY, newW = ds.origW, newH = ds.origH;
					if (h?.includes('right')) newW = Math.max(10, ds.origW + dx);
					if (h?.includes('left')) { newX = ds.origX + dx; newW = Math.max(10, ds.origW - dx); }
					if (h?.includes('bottom')) newH = Math.max(10, ds.origH + dy);
					if (h?.includes('top')) { newY = ds.origY + dy; newH = Math.max(10, ds.origH - dy); }
					groupEl.querySelectorAll('rect[data-drag]').forEach((r) => { r.setAttribute('x', String(newX)); r.setAttribute('y', String(newY)); r.setAttribute('width', String(newW)); r.setAttribute('height', String(newH)); });
					const sel = groupEl.querySelector('rect[data-sel]'); sel?.setAttribute('x', String(newX - 2)); sel?.setAttribute('y', String(newY - 2)); sel?.setAttribute('width', String(newW + 4)); sel?.setAttribute('height', String(newH + 4));
					groupEl.querySelectorAll('rect[data-handle]').forEach((handle) => {
						const name = handle.getAttribute('data-handle');
						const positions: Record<string, { cx: number; cy: number }> = { 'top-left': { cx: newX, cy: newY }, 'top-right': { cx: newX + newW, cy: newY }, 'bottom-left': { cx: newX, cy: newY + newH }, 'bottom-right': { cx: newX + newW, cy: newY + newH } };
						const p = name ? positions[name] : null;
						if (p) { handle.setAttribute('x', String(p.cx - HANDLE_SIZE / 2)); handle.setAttribute('y', String(p.cy - HANDLE_SIZE / 2)); }
					});
				}
			}
		};

		const handleMouseUp = (): void => {
			const ds = dragStateRef.current;
			if (!ds || !ann) { setDragState(null); return; }
			const { dx, dy } = dragDeltaRef.current;
			if (Math.hypot(dx, dy) > MIN_DRAG) suppressNextClickRef.current = true;
			if (groupEl) groupEl.setAttribute('transform', '');

			if (ds.mode === 'body') {
				ann.x = ds.origX + dx; ann.y = ds.origY + dy;
				if (ann.type === SkuAnnotationType.Line) { ann.x2 = ds.origX2 + dx; ann.y2 = ds.origY2 + dy; }
				ds.groupOrigins?.forEach((origin) => {
					const member = kitchen.blueprintAnnotations.findById(origin.id);
					if (!member) return;
					member.x = origin.x + dx; member.y = origin.y + dy;
					if (member.type === SkuAnnotationType.Line) { member.x2 = origin.x2 + dx; member.y2 = origin.y2 + dy; }
					const memberEl = annotationGroupRefs.current.get(origin.id);
					memberEl?.setAttribute('transform', '');
				});
			}
			else if (ds.mode === 'start-endpoint') { ann.x = ds.origX + dx; ann.y = ds.origY + dy; }
			else if (ds.mode === 'end-endpoint') { ann.x2 = ds.origX2 + dx; ann.y2 = ds.origY2 + dy; }
			else if (ds.mode === 'resize') {
				const h = ds.resizeHandle;
				let newX = ds.origX, newY = ds.origY, newW = ds.origW, newH = ds.origH;
				if (h?.includes('right')) newW = Math.max(10, ds.origW + dx);
				if (h?.includes('left')) { newX = ds.origX + dx; newW = Math.max(10, ds.origW - dx); }
				if (h?.includes('bottom')) newH = Math.max(10, ds.origH + dy);
				if (h?.includes('top')) { newY = ds.origY + dy; newH = Math.max(10, ds.origH - dy); }
				ann.x = newX; ann.y = newY; ann.width = newW; ann.height = newH;
			}
			setVersion((v) => v + 1);
			setDragState(null);
		};

		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);
		return () => { document.removeEventListener('mousemove', handleMouseMove); document.removeEventListener('mouseup', handleMouseUp); };
	}, [dragState, kitchen, screenToSvg]);

	useEffect(() => {
		if (selectedAnnotationIds.length === 0 || !kitchen) return;
		const handleKey = (e: KeyboardEvent): void => {
			if (editingAnnotationId) return;
			// Overlay <svg> no longer blocks pointer events in select mode, so a contentEditable / input on the page can now hold focus. Don't consume Delete/Backspace while the user is typing there.
			const active = document.activeElement as HTMLElement | null;
			if (active?.isContentEditable || active?.tagName === 'INPUT' || active?.tagName === 'TEXTAREA') return;
			if (e.key === 'Delete' || e.key === 'Backspace') {
				e.preventDefault();
				selectedAnnotationIds.forEach((id) => kitchen.blueprintAnnotations.delete(id));
				setSelectedAnnotationId(null);
			}
		};
		document.addEventListener('keydown', handleKey);
		return () => document.removeEventListener('keydown', handleKey);
	}, [selectedAnnotationIds, editingAnnotationId, kitchen, setSelectedAnnotationId]);

	// Close the right-click context menu (#354) on any other click or scroll
	useEffect(() => {
		if (!contextMenu) return;
		const close = (): void => setContextMenu(null);
		document.addEventListener('mousedown', close);
		document.addEventListener('scroll', close, true);
		return () => {
			document.removeEventListener('mousedown', close);
			document.removeEventListener('scroll', close, true);
		};
	}, [contextMenu, setContextMenu]);

	// Whole-box patch a picker is still dragging (#390 perf): honoured for display only, so the model is written once on commit
	const getPreviewPatch = useCallback((annId: string): AnnotationPreviewPatch | null => (
		stylePreview && stylePreview.annotationId === annId && stylePreview.range === null ? stylePreview.patch : null
	), [stylePreview]);

	const startDrag = useCallback((e: React.MouseEvent, annId: string, mode: DragState['mode'], resizeHandle?: string): void => {
		e.stopPropagation(); e.preventDefault();
		const ann = kitchen?.blueprintAnnotations?.findById(annId);
		if (!ann) return;
		// A body drag on an item already part of a multi-selection keeps that selection intact so the whole group moves together
		const isGroupDrag = mode === 'body' && selectedAnnotationIds.includes(annId) && selectedAnnotationIds.length > 1;
		if (!isGroupDrag) setSelectedAnnotationId(annId);
		const groupOrigins = isGroupDrag
			? selectedAnnotationIds.filter((id) => id !== annId).map((id) => {
				const member = kitchen?.blueprintAnnotations?.findById(id);
				return member ? { id, x: member.x, y: member.y, x2: member.x2, y2: member.y2 } : null;
			}).filter((origin): origin is { id: string; x: number; y: number; x2: number; y2: number } => origin !== null)
			: undefined;
		const ds: DragState = { annotationId: annId, mode, startClientX: e.clientX, startClientY: e.clientY, origX: ann.x, origY: ann.y, origX2: ann.x2, origY2: ann.y2, origW: ann.width, origH: ann.height, resizeHandle, groupOrigins };
		dragStateRef.current = ds;
		setDragState(ds);
	}, [kitchen, selectedAnnotationIds, setSelectedAnnotationId]);

	// PowerPoint-style rotation: mouse down on the handle starts tracking the screen-space angle around the annotation's pivot
	const handleRotateMouseDown = useCallback((e: React.MouseEvent, annId: string): void => {
		e.stopPropagation(); e.preventDefault();
		const ann = kitchen?.blueprintAnnotations?.findById(annId);
		if (!ann) return;
		setSelectedAnnotationId(annId);
		const svg = svgRef.current;
		const ctm = svg?.getScreenCTM();
		const { cx, cy } = getAnnotationCenter(ann);
		let screenCx = cx; let screenCy = cy;
		if (svg && ctm) {
			const pt = svg.createSVGPoint();
			pt.x = cx; pt.y = cy;
			const screenPt = pt.matrixTransform(ctm);
			screenCx = screenPt.x; screenCy = screenPt.y;
		}
		const startAngle = Math.atan2(e.clientY - screenCy, e.clientX - screenCx) * 180 / Math.PI;
		setRotateState({ annotationId: annId, startAngle, startRotation: ann.rotation ?? 0 });
	}, [kitchen, setSelectedAnnotationId]);

	useEffect(() => {
		if (!rotateState || !kitchen) return;
		const ann = kitchen.blueprintAnnotations.findById(rotateState.annotationId);
		if (!ann) return;

		const handleMouseMove = (e: MouseEvent): void => {
			const svg = svgRef.current;
			const ctm = svg?.getScreenCTM();
			const { cx, cy } = getAnnotationCenter(ann);
			let screenCx = cx; let screenCy = cy;
			if (svg && ctm) {
				const pt = svg.createSVGPoint();
				pt.x = cx; pt.y = cy;
				const screenPt = pt.matrixTransform(ctm);
				screenCx = screenPt.x; screenCy = screenPt.y;
			}
			const currentAngle = Math.atan2(e.clientY - screenCy, e.clientX - screenCx) * 180 / Math.PI;
			const delta = currentAngle - rotateState.startAngle;
			ann.rotation = ((rotateState.startRotation + delta) % 360 + 360) % 360;
		};

		const handleMouseUp = (): void => { suppressNextClickRef.current = true; setRotateState(null); };

		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);
		return () => { document.removeEventListener('mousemove', handleMouseMove); document.removeEventListener('mouseup', handleMouseUp); };
	}, [rotateState, kitchen]);

	const renderRotationHandle = (a: SkuAnnotation, cx: number, topY: number): React.ReactElement | null => {
		if (a.id !== selectedAnnotationId || a.id === editingAnnotationId || activeTool !== 'select') return null;
		const handleY = topY - ROTATE_HANDLE_DISTANCE;
		return (
			<>
				<line x1={cx} y1={topY} x2={cx} y2={handleY} stroke="#3182ce" strokeWidth={1} />
				<circle cx={cx} cy={handleY} r={ROTATE_HANDLE_RADIUS} fill="white" stroke="#3182ce" strokeWidth={1}
					style={{ cursor: 'grab', pointerEvents: 'auto' }}
					onMouseDown={(e) => handleRotateMouseDown(e, a.id)} />
			</>
		);
	};

	const resizeHandles = (a: SkuAnnotation): Array<React.ReactElement> => {
		if (a.id !== selectedAnnotationId || activeTool !== 'select') return [];
		const positions = [
			{ name: 'top-left', cx: a.x, cy: a.y }, { name: 'top-right', cx: a.x + a.width, cy: a.y },
			{ name: 'bottom-left', cx: a.x, cy: a.y + a.height }, { name: 'bottom-right', cx: a.x + a.width, cy: a.y + a.height }
		];
		return positions.map((p) => (
			<rect key={p.name} data-handle={p.name} data-html2canvas-ignore="true" x={p.cx - HANDLE_SIZE / 2} y={p.cy - HANDLE_SIZE / 2} width={HANDLE_SIZE} height={HANDLE_SIZE}
				fill="white" stroke="#3182ce" strokeWidth={1}
				style={{ cursor: p.name.includes('top-left') || p.name.includes('bottom-right') ? 'nwse-resize' : 'nesw-resize', pointerEvents: 'auto' }}
				onMouseDown={(e) => startDrag(e, a.id, 'resize', p.name)} />
		));
	};

	// Right-click (#354): keeps an existing multi-selection intact, otherwise mirrors handleClick's group-expansion so right-clicking any one member of a saved group selects the whole group
	const handleContextMenu = (e: React.MouseEvent, annId: string): void => {
		e.preventDefault();
		e.stopPropagation();
		if (!selectedAnnotationIds.includes(annId)) {
			const clicked = kitchen?.blueprintAnnotations?.findById(annId);
			const groupIds = clicked?.groupId
				? (kitchen?.blueprintAnnotations?.children ?? []).filter((sibling) => sibling.groupId === clicked.groupId).map((sibling) => sibling.id)
				: [annId];
			setSelectedAnnotationIds(groupIds);
		}
		setContextMenu({ x: e.clientX, y: e.clientY, annotationId: annId });
	};

	const contextMenuTargetIds = contextMenu && selectedAnnotationIds.includes(contextMenu.annotationId) ? selectedAnnotationIds : contextMenu ? [contextMenu.annotationId] : [];

	const handleContextMenuDelete = (): void => {
		contextMenuTargetIds.forEach((id) => kitchen?.blueprintAnnotations.delete(id));
		setSelectedAnnotationId(null);
		setContextMenu(null);
	};

	const handleBringToFront = (): void => {
		contextMenuTargetIds.forEach((id) => kitchen?.blueprintAnnotations.moveToFront(id));
		setVersion((v) => v + 1);
		setContextMenu(null);
	};

	const handleSendToBack = (): void => {
		[...contextMenuTargetIds].reverse().forEach((id) => kitchen?.blueprintAnnotations.moveToBack(id));
		setVersion((v) => v + 1);
		setContextMenu(null);
	};

	const handleGroup = (): void => {
		if (!kitchen || contextMenuTargetIds.length < 2) return;
		const groupId = crypto.randomUUID();
		contextMenuTargetIds.forEach((id) => { const member = kitchen.blueprintAnnotations.findById(id); if (member) member.groupId = groupId; });
		setContextMenu(null);
	};

	const handleUngroup = (): void => {
		if (!kitchen) return;
		contextMenuTargetIds.forEach((id) => { const member = kitchen.blueprintAnnotations.findById(id); if (member) member.groupId = ''; });
		setContextMenu(null);
	};

	// Rebasing onto the selection's own bounding-box origin is shared with the toolbar's save button and with placeAnnotationPreset
	const handleSaveToPresets = (): void => {
		if (!kitchen || !onSaveAnnotationPreset || contextMenuTargetIds.length === 0) return;
		const members = contextMenuTargetIds.map((id) => kitchen.blueprintAnnotations.findById(id)).filter((a): a is SkuAnnotation => a !== null);
		if (members.length === 0) return;
		const name = window.prompt('Save preset as:', 'Untitled preset');
		if (!name) return;
		// A right-click inside the textarea leaves the edit uncommitted, and its text lives in the editor until then
		pendingSaveRef.current?.();
		onSaveAnnotationPreset(name, toAnnotationPresetRecords({ members, isSkuSurface: false }));
		setContextMenu(null);
	};

	const contextMenuGroupId = contextMenu ? kitchen?.blueprintAnnotations.findById(contextMenu.annotationId)?.groupId ?? '' : '';

	const renderAnnotation = (a: SkuAnnotation): React.ReactElement | null => {
		const isSel = selectedAnnotationIds.includes(a.id);
		const isEdit = a.id === editingAnnotationId;
		const dashArr = getDashArray(a.lineStyle);
		const setGroupRef = (el: SVGGElement | null): void => { if (el) { annotationGroupRefs.current.set(a.id, el); } else { annotationGroupRefs.current.delete(a.id); } };
		const handleClick = (e: React.MouseEvent): void => {
			e.stopPropagation();
			if (suppressNextClickRef.current) { suppressNextClickRef.current = false; return; }
			if (activeTool !== 'select') return;
			if (e.shiftKey || e.metaKey || e.ctrlKey) { toggleAnnotationSelection(a.id, true); return; }
			// Selecting one member of a saved group re-selects the whole group so it moves/right-clicks as a unit
			if (a.groupId) {
				const groupIds = (kitchen?.blueprintAnnotations?.children ?? []).filter((sibling) => sibling.groupId === a.groupId).map((sibling) => sibling.id);
				setSelectedAnnotationIds(groupIds);
				return;
			}
			setSelectedAnnotationId(a.id);
		};
		// A double-click INSIDE a live session is a word selection, not a request to start one -- clearing formatSelection there is what made a format land on the whole box
		const handleDblClick = (e: React.MouseEvent): void => { e.stopPropagation(); if (isEdit || a.type !== SkuAnnotationType.TextBox || activeTool !== 'select') return; setSelectedAnnotationId(a.id); setEditingAnnotationId(a.id); setActiveTextSelection(null); setFormatSelection(null); };

		if (a.type === SkuAnnotationType.TextBox) {
			const { cx, cy } = getAnnotationCenter(a);
			const textPreview = getPreviewPatch(a.id);
			return (
				<g key={a.id} ref={setGroupRef} data-bp-annotation="true" transform={a.rotation ? `rotate(${a.rotation}, ${cx}, ${cy})` : undefined} onClick={handleClick} onDoubleClick={handleDblClick} onContextMenu={(e) => handleContextMenu(e, a.id)}>
					{isSel && <rect data-sel data-html2canvas-ignore="true" x={a.x - 2} y={a.y - 2} width={a.width + 4} height={a.height + 4} fill="none" stroke="#3182ce" strokeWidth={1} strokeDasharray="4,3" style={{ pointerEvents: 'none' }} />}
					{/* Hit area: a text box with neither fill nor border must still be grabbable */}
					<rect data-drag="hit" x={a.x} y={a.y} width={a.width} height={a.height} fill="transparent" stroke="none" rx={2}
						style={{ cursor: activeTool === 'select' ? 'move' : 'default', pointerEvents: 'auto' }}
						onMouseDown={(e) => { if (activeTool === 'select' && !isEdit) startDrag(e, a.id, 'body'); }} />
					<rect data-drag="vis" x={a.x} y={a.y} width={a.width} height={a.height} {...getAnnotationFillPaint({ annotation: a, previewColor: textPreview?.fillColor })} {...getAnnotationStrokePaint({ annotation: a, width: TEXT_BOX_STROKE_WIDTH, dasharray: dashArr, previewColor: textPreview?.strokeColor })} rx={2} style={{ pointerEvents: 'none' }} />
					<BlueprintPageAnnotationText annotation={a} signature={getTextSignature(a)} isEditing={isEdit} isSelected={isSel} previewPatch={textPreview} onCommit={bumpVersion} />
					{...resizeHandles(a)}
					{renderRotationHandle(a, cx, a.y)}
				</g>
			);
		}
		if (a.type === SkuAnnotationType.Line) {
			const hasStartArrow = a.arrowhead === SkuAnnotationArrowhead.Start || a.arrowhead === SkuAnnotationArrowhead.Both;
			const hasEndArrow = a.arrowhead === SkuAnnotationArrowhead.End || a.arrowhead === SkuAnnotationArrowhead.Both;
			const mid = `bp-arrow-${a.id}`;
			const { cx: lineCx, cy: lineCy } = getAnnotationCenter(a);
			const { stroke, strokeWidth, strokeDasharray } = getAnnotationStrokePaint({ annotation: a, width: a.strokeWidth, dasharray: dashArr, previewColor: getPreviewPatch(a.id)?.strokeColor });
			return (
				<g key={a.id} ref={setGroupRef} data-bp-annotation="true" transform={a.rotation ? `rotate(${a.rotation}, ${lineCx}, ${lineCy})` : undefined} onClick={handleClick} onContextMenu={(e) => handleContextMenu(e, a.id)}>
					<defs>
						<marker id={`${mid}-end`} markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto" markerUnits="strokeWidth"><polygon points="0 0, 10 3.5, 0 7" fill={stroke} /></marker>
						<marker id={`${mid}-start`} markerWidth="10" markerHeight="7" refX="1" refY="3.5" orient="auto" markerUnits="strokeWidth"><polygon points="10 0, 0 3.5, 10 7" fill={stroke} /></marker>
					</defs>
					<line data-drag="hit" x1={a.x} y1={a.y} x2={a.x2} y2={a.y2} stroke="transparent" strokeWidth={Math.max(a.strokeWidth * 5, 8)} style={{ pointerEvents: 'stroke', cursor: activeTool === 'select' ? 'move' : 'default' }} onMouseDown={(e) => { if (activeTool === 'select') startDrag(e, a.id, 'body'); }} />
					<line data-drag="vis" x1={a.x} y1={a.y} x2={a.x2} y2={a.y2} stroke={stroke} strokeWidth={strokeWidth} strokeDasharray={strokeDasharray} markerStart={hasStartArrow ? `url(#${mid}-start)` : undefined} markerEnd={hasEndArrow ? `url(#${mid}-end)` : undefined} style={{ pointerEvents: 'none' }} />
					{isSel && <line data-drag="sel" x1={a.x} y1={a.y} x2={a.x2} y2={a.y2} stroke="#3182ce" strokeWidth={a.strokeWidth + 2} strokeDasharray="4,3" opacity={0.4} style={{ pointerEvents: 'none' }} />}
					{isSel && activeTool === 'select' && (<>
						<circle data-endpoint="start" data-html2canvas-ignore="true" cx={a.x} cy={a.y} r={5} fill="white" stroke="#3182ce" strokeWidth={1} style={{ cursor: 'crosshair', pointerEvents: 'auto' }} onMouseDown={(e) => startDrag(e, a.id, 'start-endpoint')} />
						<circle data-endpoint="end" data-html2canvas-ignore="true" cx={a.x2} cy={a.y2} r={5} fill="white" stroke="#3182ce" strokeWidth={1} style={{ cursor: 'crosshair', pointerEvents: 'auto' }} onMouseDown={(e) => startDrag(e, a.id, 'end-endpoint')} />
					</>)}
					{renderRotationHandle(a, lineCx, lineCy)}
				</g>
			);
		}
		if (a.type === SkuAnnotationType.Doodle) {
			const d = a.points.reduce((p, pt, i) => i === 0 ? `M ${pt.x + a.x} ${pt.y + a.y}` : `${p} L ${pt.x + a.x} ${pt.y + a.y}`, '');
			return (
				<g key={a.id} ref={setGroupRef} data-bp-annotation="true" onClick={handleClick} onContextMenu={(e) => handleContextMenu(e, a.id)}>
					<path d={d} fill="none" stroke="transparent" strokeWidth={Math.max(a.strokeWidth * 3, 6)} style={{ pointerEvents: 'stroke', cursor: activeTool === 'select' ? 'pointer' : 'default' }} onMouseDown={(e) => { if (activeTool === 'select') startDrag(e, a.id, 'body'); }} />
					<path d={d} fill="none" {...getAnnotationStrokePaint({ annotation: a, width: a.strokeWidth, dasharray: dashArr, previewColor: getPreviewPatch(a.id)?.strokeColor })} strokeLinecap="round" strokeLinejoin="round" style={{ pointerEvents: 'none' }} />
					{isSel && <path data-html2canvas-ignore="true" d={d} fill="none" stroke="#3182ce" strokeWidth={a.strokeWidth + 1} strokeDasharray="4,3" opacity={0.4} style={{ pointerEvents: 'none' }} />}
				</g>
			);
		}
		if (a.type === SkuAnnotationType.Rectangle) {
			const { cx: rectCx, cy: rectCy } = getAnnotationCenter(a);
			const rectPreview = getPreviewPatch(a.id);
			return (
				<g key={a.id} ref={setGroupRef} data-bp-annotation="true" transform={a.rotation ? `rotate(${a.rotation}, ${rectCx}, ${rectCy})` : undefined} onClick={handleClick} onContextMenu={(e) => handleContextMenu(e, a.id)}>
					{/* Hit area: an unfilled shape is grabbed by its (floored) stroke band, so it stops swallowing interior clicks */}
					<rect data-drag="hit" x={a.x} y={a.y} width={a.width} height={a.height} fill="transparent" stroke="transparent" strokeWidth={Math.max(a.strokeWidth * 3, 6)} style={{ pointerEvents: isAnnotationFilled(a) ? 'auto' : 'stroke', cursor: activeTool === 'select' ? 'move' : 'default' }} onMouseDown={(e) => { if (activeTool === 'select') startDrag(e, a.id, 'body'); }} />
					<rect data-drag="vis" x={a.x} y={a.y} width={a.width} height={a.height} {...getAnnotationFillPaint({ annotation: a, previewColor: rectPreview?.fillColor })} {...getAnnotationStrokePaint({ annotation: a, width: a.strokeWidth, dasharray: dashArr, previewColor: rectPreview?.strokeColor })} rx={1} style={{ pointerEvents: 'none' }} />
					{isSel && <rect data-sel data-html2canvas-ignore="true" x={a.x - 2} y={a.y - 2} width={a.width + 4} height={a.height + 4} fill="none" stroke="#3182ce" strokeWidth={1} strokeDasharray="4,3" style={{ pointerEvents: 'none' }} />}
					{...resizeHandles(a)}
					{renderRotationHandle(a, rectCx, a.y)}
				</g>
			);
		}
		if (a.type === SkuAnnotationType.Ellipse) {
			const { x, y, width, height, strokeWidth: sw, rotation } = a;
			const cx = x + width / 2; const cy = y + height / 2;
			const ellipsePreview = getPreviewPatch(a.id);
			return (
				<g key={a.id} ref={setGroupRef} data-bp-annotation="true" transform={rotation ? `rotate(${rotation}, ${cx}, ${cy})` : undefined} onClick={handleClick} onContextMenu={(e) => handleContextMenu(e, a.id)}>
					{/* Hit area: an unfilled shape is grabbed by its (floored) stroke band, so it stops swallowing interior clicks */}
					<ellipse cx={cx} cy={cy} rx={width / 2} ry={height / 2} fill="transparent" stroke="transparent" strokeWidth={Math.max(sw * 3, 6)} style={{ pointerEvents: isAnnotationFilled(a) ? 'auto' : 'stroke', cursor: activeTool === 'select' ? 'move' : 'default' }} onMouseDown={(e) => { if (activeTool === 'select') startDrag(e, a.id, 'body'); }} />
					<ellipse cx={cx} cy={cy} rx={width / 2} ry={height / 2} {...getAnnotationFillPaint({ annotation: a, previewColor: ellipsePreview?.fillColor })} {...getAnnotationStrokePaint({ annotation: a, width: sw, dasharray: dashArr, previewColor: ellipsePreview?.strokeColor })} style={{ pointerEvents: 'none' }} />
					{isSel && <rect data-sel data-html2canvas-ignore="true" x={x - 2} y={y - 2} width={width + 4} height={height + 4} fill="none" stroke="#3182ce" strokeWidth={1} strokeDasharray="4,3" style={{ pointerEvents: 'none' }} />}
					{...resizeHandles(a)}
					{renderRotationHandle(a, cx, y)}
				</g>
			);
		}
		return null;
	};

	const isDrawTool = activeTool !== 'select';

	// pointer-events: only 'auto' when a draw tool needs to capture new drags. In select mode, individual annotation shapes opt in via their own `pointerEvents: 'auto'`, so a full-bleed 'auto' would only block the contentEditable page content underneath (was blocking "Additional Notes" edits once any annotation existed on the page).
	const overlaySvg = (
		<svg ref={svgRef} viewBox={`0 0 ${SVG_W} ${SVG_H}`} preserveAspectRatio="none"
			style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: isDrawTool ? 'auto' : 'none', cursor: isDrawTool ? 'crosshair' : 'default', zIndex: 5 }}
			onMouseDown={isDrawTool ? handleDrawMouseDown : undefined} onMouseMove={isDrawTool ? handleDrawMouseMove : undefined}
			onMouseUp={isDrawTool ? handleDrawMouseUp : undefined} onMouseLeave={isDrawTool ? handleDrawMouseUp : undefined}
			onClick={(e) => { if (activeTool === 'select') { const target = e.target as SVGElement; if (target === svgRef.current) { setSelectedAnnotationId(null); setEditingAnnotationId(null); } } }}
		>
			{annotations.map((a) => renderAnnotation(a))}
			<rect ref={textPreviewRef} x={0} y={0} width={0} height={0} fill="rgba(49,130,206,0.08)" stroke="#3182ce" strokeWidth={1} strokeDasharray="4,3" visibility="hidden" style={{ pointerEvents: 'none' }} />
			<path ref={doodlePreviewRef} d="" fill="none" stroke="#e53e3e" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" visibility="hidden" style={{ pointerEvents: 'none' }} />
			<line ref={linePreviewRef} x1={0} y1={0} x2={0} y2={0} stroke="#3182ce" strokeWidth={1} strokeDasharray="4,3" visibility="hidden" style={{ pointerEvents: 'none' }} />
			<rect ref={rectPreviewRef} x={0} y={0} width={0} height={0} fill="none" stroke="#3182ce" strokeWidth={1} strokeDasharray="4,3" visibility="hidden" style={{ pointerEvents: 'none' }} />
			<ellipse ref={ellipsePreviewRef} cx={0} cy={0} rx={0} ry={0} fill="none" stroke="#3182ce" strokeWidth={1} strokeDasharray="4,3" visibility="hidden" style={{ pointerEvents: 'none' }} />
		</svg>
	);

	const menuButtonStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', width: '100%', padding: '8px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '13px', textAlign: 'left' };

	return (
		<>
			{children}
			{portalTarget ? createPortal(overlaySvg, portalTarget) : null}
			{/* Annotation right-click context menu (#354): stack control, grouping, save-to-presets, delete */}
			{contextMenu && createPortal(
				<div
					data-annotation-context-menu="true"
					style={{ position: 'fixed', top: contextMenu.y, left: contextMenu.x, zIndex: 10000, background: 'white', border: '1px solid #ccc', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', padding: '4px 0', minWidth: '160px', fontSize: '13px' }}
					onMouseDown={(e) => e.stopPropagation()}
				>
					<button onClick={handleBringToFront} style={menuButtonStyle}>Bring to Front</button>
					<button onClick={handleSendToBack} style={menuButtonStyle}>Send to Back</button>
					{contextMenuTargetIds.length > 1 && (
						<button onClick={handleGroup} style={menuButtonStyle}>Group</button>
					)}
					{contextMenuGroupId && (
						<button onClick={handleUngroup} style={menuButtonStyle}>Ungroup</button>
					)}
					{onSaveAnnotationPreset && (
						<button onClick={handleSaveToPresets} style={menuButtonStyle}>Save to presets</button>
					)}
					<button onClick={handleContextMenuDelete} style={{ ...menuButtonStyle, color: '#e53e3e' }}>Delete</button>
				</div>,
				document.body
			)}
		</>
	);
};
